import type { TRANSLATIONS_DE } from "./translations_DE"

export type TranslationKeys = keyof typeof TRANSLATIONS_DE
export type TranslationCatalog = Record<TranslationKeys, string>

/** Every language with a catalog. JA stays loadable although it is hidden
 *  from the language picker (see `Languages` in useTranslation.tsx). */
export type CatalogLanguage = "DE" | "EN" | "JA"
export const CATALOG_LANGUAGES: readonly CatalogLanguage[] = ["DE", "EN", "JA"]

// Each catalog is ~500 KB of source. Importing all three statically shipped
// every language on every page load; each now loads as its own chunk, on
// demand. The app renders once the UI language's catalog is in (index.tsx).
const loaders: Record<CatalogLanguage, () => Promise<TranslationCatalog>> = {
  DE: () => import("./translations_DE").then((m) => m.TRANSLATIONS_DE),
  EN: () => import("./translations_EN").then((m) => m.TRANSLATIONS_EN),
  JA: () => import("./translations_JA").then((m) => m.TRANSLATIONS_JA),
}

const catalogs: Partial<Record<CatalogLanguage, TranslationCatalog>> = {}
const inFlight: Partial<Record<CatalogLanguage, Promise<TranslationCatalog>>> =
  {}

/** The catalog for `language` once loaded, else undefined — callers fall
 *  back to the key, the same as for a missing translation. */
export function getCatalog(
  language: CatalogLanguage,
): TranslationCatalog | undefined {
  return catalogs[language]
}

export function loadCatalog(
  language: CatalogLanguage,
): Promise<TranslationCatalog> {
  const loaded = catalogs[language]
  if (loaded) return Promise.resolve(loaded)
  inFlight[language] ??= loaders[language]().then(
    (catalog) => {
      catalogs[language] = catalog
      delete inFlight[language]
      return catalog
    },
    (error: unknown) => {
      // Forget the failed attempt so the next call retries.
      delete inFlight[language]
      throw error
    },
  )
  return inFlight[language]
}

export function loadCatalogs(
  languages: readonly CatalogLanguage[],
): Promise<TranslationCatalog[]> {
  return Promise.all(languages.map(loadCatalog))
}

/** Makes catalogs available synchronously up front, for environments that
 *  render without the async bootstrap: the test setup and Storybook. */
export function registerCatalogs(
  entries: Partial<Record<CatalogLanguage, TranslationCatalog>>,
): void {
  Object.assign(catalogs, entries)
}
